/**
 * PropertiesPanel Icon Section patch
 *
 * Replace the icon section inside PropertiesPanelBasic with this code.
 * The key wiring:
 *
 *   value  → getCurrentIconName(selectedElement)   converts iconContent to PascalCase
 *   onChange → onIconChange(iconName)              receives PascalCase, ProjectView generates SVG
 *
 * This means:
 *   - IconSelect shows the correct current icon pre-selected
 *   - Picking a new icon calls handleIconChange in ProjectView with PascalCase name
 *   - ProjectView generates the SVG string from lucide-react and sends to iframe
 *   - No SVG string ever passes through IconSelect (it only deals with names)
 */

// ─── In PropertiesPanel.jsx, update the icon section inside PropertiesPanelBasic ───

// 1. Add this import at the top of PropertiesPanel.jsx:
//    import IconSelect, { getCurrentIconName } from "../IconSelect";

// 2. Replace the isIconElement section with the code below:

/*

{isIconElement && (
  <div
    className="design-drawer-section accordion-section"
    style={{ marginTop: "12px", borderRadius: "10px", overflow: "hidden", border: "1px solid #e5e7eb" }}
  >
    <div
      className="section-header accordion-header"
      onClick={() => onToggleSection("icon")}
      style={{ cursor: "pointer", userSelect: "none", padding: "10px 12px", background: "#f9fafb" }}
    >
      <h6 style={{ margin: 0, fontSize: "13px", fontWeight: "600", color: "#374151", display: "flex", alignItems: "center" }}>
        <i className="fa-solid fa-icons" style={{ marginRight: "8px", color: "#6366f1" }} />
        Icon
      </h6>
      <i
        className={`fa-solid ${state.expandedSections.icon ? "fa-chevron-up" : "fa-chevron-down"}`}
        style={{ color: "#9ca3af", fontSize: "11px" }}
      />
    </div>
    {state.expandedSections.icon && (
      <div
        className="accordion-content"
        style={{ padding: "12px 14px", background: "#fff", borderTop: "1px solid #e5e7eb" }}
      >
        <IconSelect
          value={getCurrentIconName(state.selectedElement)}
          onChange={(iconName) => {
            // iconName is PascalCase ("ArrowRight") — pass directly to handler
            // ProjectView.handleIconChange will generate the SVG and send to iframe
            if (onIconChange && iconName) onIconChange(iconName);
          }}
        />
      </div>
    )}
  </div>
)}

*/

// ─── Full updated PropertiesPanel.jsx ─────────────────────────────────────────

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { PiSpinnerGapLight } from "react-icons/pi";
import { ALLOWED_IMAGE_MIME_TYPES, MAX_IMAGE_UPLOAD_SIZE } from "../../settings/shared/constants";
import { formatMB } from "../../utils/fileValidation";
import IconSelect, { getCurrentIconName } from "../IconSelect";
import OrchestratorOnClickPanel from "./OrchestratorOnClickPanel";

const buildImageFilter = (filters) =>
  `brightness(${filters.brightness}%) contrast(${filters.contrast}%) saturate(${filters.saturation}%) blur(${filters.blur}px) hue-rotate(${filters.hue}deg)`.trim();

const ImageSlider = ({ label, min, max, step = 1, value, onChange, formatValue }) => {
  const percentage   = ((value - min) / (max - min)) * 100;
  const displayValue = formatValue ? formatValue(value) : value;
  return (
    <div className="image-slider-container" style={{ marginBottom: "14px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
        <span className="image-slider-label" style={{ fontSize: "12px", color: "#6b7280", fontWeight: "500" }}>{label}</span>
        <span style={{ fontSize: "11px", fontWeight: "600", color: "#1d4ed8", background: "#eff6ff", padding: "2px 8px", borderRadius: "6px", minWidth: "36px", textAlign: "center" }}>{displayValue}</span>
      </div>
      <div className="image-slider-wrapper" style={{ position: "relative", width: "100%", paddingBottom: "4px" }}>
        <div className="image-slider-fill" style={{ position: "absolute", left: 0, top: "50%", transform: "translateY(-50%)", width: `${percentage}%`, height: "4px", backgroundColor: "#60a5fa", borderRadius: "2px", pointerEvents: "none", zIndex: 0 }} />
        <input type="range" className="image-slider-input" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} style={{ position: "relative", zIndex: 1, width: "100%", margin: 0, accentColor: "#2563eb" }} />
      </div>
    </div>
  );
};

function AccordionSection({ id, icon, title, expanded, onToggle, children, hint }) {
  return (
    <section className={`props-section ${expanded ? "props-section--open" : ""}`}>
      <button
        type="button"
        className="props-section__header"
        onClick={() => onToggle(id)}
        aria-expanded={expanded}
      >
        <span className="props-section__title">
          <span className="props-section__icon" aria-hidden="true">
            <i className={`fa-solid ${icon}`} />
          </span>
          {title}
        </span>
        <i
          className={`fa-solid fa-chevron-${expanded ? "up" : "down"} props-section__chevron`}
          aria-hidden="true"
        />
      </button>
      {expanded && (
        <div className="props-section__body">
          {hint ? <p className="props-section__hint">{hint}</p> : null}
          {children}
        </div>
      )}
    </section>
  );
}

function TextContentEditor({ elementId, initialText, onTextContentChange }) {
  const [text, setText] = useState(initialText ?? "");
  const syncedElementIdRef = useRef(elementId);

  useEffect(() => {
    if (syncedElementIdRef.current === elementId) return;
    syncedElementIdRef.current = elementId;
    setText(initialText ?? "");
  }, [elementId, initialText]);

  return (
    <textarea
      className="text-editor-input props-textarea"
      value={text}
      onChange={(e) => {
        const value = e.target.value;
        setText(value);
        onTextContentChange(value);
      }}
      placeholder="Edit text shown in the preview…"
      rows={3}
    />
  );
}

function getElementType(element) {
  const tag     = element?.tag || element?.tagName?.toLowerCase() || element?.tagName || element?.elementType || element?.type || "";
  const tagLower = String(tag).toLowerCase();
  if (tagLower === "icon" || tagLower === "svg") return "icon";
  if (tagLower === "button" || tagLower === "btn") return "button";
  if (["p","h1","h2","h3","h4","h5","h6","span","label","a","li","td","th","strong","em","b","i"].includes(tagLower)) return "text";
  if (["input","textarea","select"].includes(tagLower)) return "input";
  if (["img","image"].includes(tagLower)) return "image";
  if (["div","section","article","header","footer","nav","main","aside"].includes(tagLower)) return "container";
  return "other";
}

const ELEMENT_TYPE_META = {
  button:    { label: "Button",    icon: "fa-square",       tone: "blue" },
  text:      { label: "Text",      icon: "fa-font",         tone: "teal" },
  input:     { label: "Input",     icon: "fa-i-cursor",     tone: "amber" },
  image:     { label: "Image",     icon: "fa-image",        tone: "rose" },
  icon:      { label: "Icon",      icon: "fa-icons",        tone: "indigo" },
  container: { label: "Container", icon: "fa-object-group", tone: "slate" },
  other:     { label: "Element",   icon: "fa-cube",         tone: "slate" },
};

function PropertiesPanel({
  state, setState, onClose, onToggleSection, onTextContentChange, onPropertyChange,
  onIconChange, parseBorder, onBorderPropertyChange, adjustBorderWidth, adjustNumericValue,
  onDrop, onDragOver, onFileInputChange, onImageUpload, onUpdateImageFilter,
  applyCrop, applyTransform, onResetFilters, formatMB: formatMBProp, maxImageUploadSize, allowedImageMimeTypes,
  availableWorkflows, availableServiceApps, uiEventSaving, selectedElementSelectorKey, selectedElementEventConfig, onUpsertElementEventConfig,
  projectId, onOrchestratorOnClickChange,
}) {
  const maxSize        = maxImageUploadSize != null ? maxImageUploadSize : MAX_IMAGE_UPLOAD_SIZE;
  const mimeTypes      = allowedImageMimeTypes || ALLOWED_IMAGE_MIME_TYPES;
  const formatMBLabel  = formatMBProp ? formatMBProp(maxSize) : formatMB(maxSize);

  const drawerContentRef   = useRef(null);
  const scrollTopRef       = useRef(0);
  const lastSelectionIdRef = useRef(null);

  const elementType = getElementType(state.selectedElement);
  const typeMeta = ELEMENT_TYPE_META[elementType] || ELEMENT_TYPE_META.other;
  const tagLabel =
    state.selectedElement?.mapTagName ||
    state.selectedElement?.tagName ||
    state.selectedElement?.tag ||
    "Element";

  const handleDrawerScroll = (e) => {
    scrollTopRef.current = e.currentTarget.scrollTop;
  };

  // Re-renders from property edits reset scroll on `.drawer-content`; restore it
  // while the same element stays selected. Reset scroll when selection changes.
  useLayoutEffect(() => {
    const el = drawerContentRef.current;
    if (!el) return;
    const id = state.selectedElement?.id;
    if (lastSelectionIdRef.current !== id) {
      lastSelectionIdRef.current = id;
      scrollTopRef.current = 0;
      el.scrollTop = 0;
      return;
    }
    el.scrollTop = scrollTopRef.current;
  }, [state.selectedElement?.id]);

  return (
    <aside
      className="drawer-panel drawer-panel--docked drawer-panel--left props-drawer"
      aria-label="Element properties"
    >
      <header className="props-drawer__header">
        <div className={`props-drawer__type-badge props-drawer__type-badge--${typeMeta.tone}`} aria-hidden="true">
          <i className={`fa-solid ${typeMeta.icon}`} />
        </div>
        <div className="props-drawer__selection-copy">
          <div className="props-drawer__kicker">Properties</div>
          <h3 className="props-drawer__title">
            {typeMeta.label}
            <span className="props-drawer__tag">&lt;{String(tagLabel).toLowerCase()}&gt;</span>
          </h3>
        </div>
        <button
          type="button"
          className="props-drawer__close"
          onClick={onClose}
          aria-label="Close properties panel"
          title="Back to chat"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </header>

      <div
        ref={drawerContentRef}
        className="drawer-content props-drawer__content"
        onScroll={handleDrawerScroll}
        style={{ overflowY: "auto", overflowAnchor: "none" }}
      >
        <div className="props-drawer__stack">
          {state.selectedElement ? (
            <>
              <PropertiesPanelBasic
                state={state}
                setState={setState}
                onToggleSection={onToggleSection}
                onTextContentChange={onTextContentChange}
                onPropertyChange={onPropertyChange}
                onIconChange={onIconChange}
                parseBorder={parseBorder}
                onBorderPropertyChange={onBorderPropertyChange}
                adjustBorderWidth={adjustBorderWidth}
                adjustNumericValue={adjustNumericValue}
                getElementType={getElementType}
                availableWorkflows={availableWorkflows}
                availableServiceApps={availableServiceApps}
                uiEventSaving={uiEventSaving}
                selectedElementSelectorKey={selectedElementSelectorKey}
                selectedElementEventConfig={selectedElementEventConfig}
                onUpsertElementEventConfig={onUpsertElementEventConfig}
                projectId={projectId}
                onOrchestratorOnClickChange={onOrchestratorOnClickChange}
              />
              {getElementType(state.selectedElement) === "image" && (
                <PropertiesPanelAdvanced
                  state={state} setState={setState} onToggleSection={onToggleSection}
                  onDrop={onDrop} onDragOver={onDragOver} onFileInputChange={onFileInputChange}
                  onImageUpload={onImageUpload} onUpdateImageFilter={onUpdateImageFilter}
                  applyCrop={applyCrop} applyTransform={applyTransform} onResetFilters={onResetFilters}
                  ImageSlider={ImageSlider} buildImageFilter={buildImageFilter}
                  formatMBLabel={formatMBLabel} allowedImageMimeTypes={mimeTypes}
                />
              )}
            </>
          ) : (
            <div className="props-drawer__empty">
              <div className="props-drawer__empty-icon" aria-hidden="true">
                <i className="fa-solid fa-mouse-pointer" />
              </div>
              <p className="props-drawer__empty-title">Select something to edit</p>
              <p className="props-drawer__empty-sub">
                Click any element in the preview to inspect and change its properties.
              </p>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}

function PropertiesPanelBasic({
  state, setState, onToggleSection, onTextContentChange, onPropertyChange, onIconChange,
  parseBorder, onBorderPropertyChange, adjustBorderWidth, adjustNumericValue, getElementType,
  availableWorkflows, availableServiceApps, uiEventSaving, selectedElementSelectorKey, selectedElementEventConfig, onUpsertElementEventConfig,
  projectId, onOrchestratorOnClickChange,
}) {
  const elementType = getElementType(state.selectedElement);
  const isButton    = elementType === "button";
  const isText      = elementType === "text";
  const isInput     = elementType === "input";
  const isImage     = elementType === "image";
  const isContainer = elementType === "container";
  const isIconElement = elementType === "icon";
  const showContent   = isButton || isText || isInput;
  const showTypography = isText || isInput;
  const showButtonProps = isButton || isContainer;
  const canAssignOnClick =
    isButton || isContainer ||
    (state.selectedElement?.tag || "").toLowerCase() === "a" ||
    (state.selectedElement?.type || "").toLowerCase() === "link";
  const showBasicInfo   = true;

  // ── Derive current icon name for IconSelect ───────────────────────────────
  // getCurrentIconName handles:
  //   "lucide-file-text" → "FileText"
  //   "FileText"         → "FileText"
  //   "<svg ...>"        → null
  //   className fallback → "FileText"
  const currentIconName = getCurrentIconName(state.selectedElement);

  // ──────────────────────────────────────────────────────────────────────────
  // Button event mapping (scalable: events object supports future onFocus/onBlur)
  // ──────────────────────────────────────────────────────────────────────────

  const selectorKey = selectedElementSelectorKey || null;
  const selector = useMemo(() => {
    const key = selectorKey;
    if (!key) return null;
    if (key.startsWith("data-element-id:")) return { type: "data-element-id", value: key.slice("data-element-id:".length) };
    if (key.startsWith("id:")) return { type: "id", value: key.slice(3) };
    if (key.startsWith("css:")) return { type: "css", value: key.slice(4) };
    if (key.startsWith("runtime:")) return { type: "runtime", value: key.slice(8) };
    return { type: "unknown", value: key };
  }, [selectorKey]);

  const defaultOnClickDraft = useMemo(() => ({
    enabled: true,
    actionType: "workflow",
    workflowId: "",
    workflowName: "",
    query: "",
    executionType: "ORCHESTRATOR", // ORCHESTRATOR | SERVICE
    orchestrator: {
      method: "POST",
      endpointTemplate: "/orchestrator/execute/{workflowId}",
      query: "",
    },
    service: {
      serviceAppId: "",
    },
  }), []);

  const [onClickDraft, setOnClickDraft] = useState(defaultOnClickDraft);

  useEffect(() => {
    const saved = selectedElementEventConfig?.onClick || null;
    if (!saved) {
      setOnClickDraft(defaultOnClickDraft);
      return;
    }

    setOnClickDraft({
      ...defaultOnClickDraft,
      ...saved,
      workflowId: String(saved.workflowId || ""),
      workflowName: saved.workflowName || "",
      query: String(saved.query ?? saved.orchestrator?.query ?? ""),
      orchestrator: {
        ...defaultOnClickDraft.orchestrator,
        ...(saved.orchestrator || {}),
        query: String(saved.query ?? saved.orchestrator?.query ?? ""),
      },
      service: {
        ...defaultOnClickDraft.service,
        ...(saved.service || {}),
      },
    });
  }, [
    state.selectedElement?.id,
    selectedElementSelectorKey,
    selectedElementEventConfig,
    defaultOnClickDraft,
  ]);

  const canSaveOnClick =
    Boolean(selectorKey) &&
    Boolean(onClickDraft.workflowId) &&
    (onClickDraft.executionType !== "SERVICE" || Boolean(onClickDraft.service?.serviceAppId));

  const cleanedOnClick = useMemo(() => {
    const d = onClickDraft || {};
    const query = String(d.query ?? d.orchestrator?.query ?? "");
    const next = {
      enabled: Boolean(d.enabled),
      actionType: "workflow",
      workflowId: String(d.workflowId || ""),
      workflowName: String(d.workflowName || ""),
      query,
      executionType: d.executionType === "SERVICE" ? "SERVICE" : "ORCHESTRATOR",
    };

    if (next.executionType === "ORCHESTRATOR") {
      next.orchestrator = {
        method: d.orchestrator?.method || "POST",
        endpointTemplate: d.orchestrator?.endpointTemplate || "/orchestrator/execute/{workflowId}",
        query,
      };
    } else {
      next.service = {
        serviceAppId: String(d.service?.serviceAppId || ""),
      };
    }
    return next;
  }, [onClickDraft]);

  const isOnClickDirty = useMemo(() => {
    const saved = selectedElementEventConfig?.onClick || null;
    try {
      return JSON.stringify(saved || null) !== JSON.stringify(cleanedOnClick);
    } catch {
      return true;
    }
  }, [selectedElementEventConfig?.onClick, cleanedOnClick]);

  // Auto-save onClick config (debounced)
  useEffect(() => {
    if (!onUpsertElementEventConfig) return;
    if (!selectorKey) return;
    if (!canSaveOnClick) return;
    if (!isOnClickDirty) return;
    if (uiEventSaving) return;

    const t = setTimeout(() => {
      const nextEvents = {
        ...(selectedElementEventConfig || {}),
        onClick: cleanedOnClick,
      };
      onUpsertElementEventConfig(selectorKey, selector, nextEvents);
    }, 600);

    return () => clearTimeout(t);
  }, [
    canSaveOnClick,
    isOnClickDirty,
    uiEventSaving,
    selectorKey,
    selector,
    cleanedOnClick,
    selectedElementEventConfig,
    onUpsertElementEventConfig,
  ]);

  return (
    <>
      {/* Content (text) */}
      {showContent && (
        <AccordionSection
          id="content"
          icon="fa-align-left"
          title="Content"
          hint="Edit the visible text for this element."
          expanded={Boolean(state.expandedSections.content)}
          onToggle={onToggleSection}
        >
          <div className="text-editor-controls">
            <TextContentEditor
              elementId={state.selectedElement?.id}
              initialText={
                state.selectedElement?.textContent ||
                state.selectedElement?.text ||
                state.selectedElement?.innerText ||
                state.selectedElement?.content ||
                ""
              }
              onTextContentChange={onTextContentChange}
            />
          </div>
        </AccordionSection>
      )}

      {/* Icon picker — only shown when an SVG is selected */}
      {isIconElement && (
        <AccordionSection
          id="icon"
          icon="fa-icons"
          title="Icon"
          hint="Swap this Lucide icon. Preview updates immediately."
          expanded={Boolean(state.expandedSections.icon)}
          onToggle={onToggleSection}
        >
          <div className="props-current-chip">
            {currentIconName
              ? <>Current · <strong>{currentIconName}</strong></>
              : "No named icon detected — pick one below"}
          </div>
          <IconSelect
            value={currentIconName}
            onChange={(iconName) => {
              if (onIconChange && iconName) onIconChange(iconName);
            }}
          />
        </AccordionSection>
      )}

      {/* Button Properties */}
      {showButtonProps && (
        <AccordionSection id="buttonProperties" icon="fa-square" title="Appearance" hint="Colors, size, and border for this control." expanded={Boolean(state.expandedSections.buttonProperties)} onToggle={onToggleSection}>
          <div className="properties-form">
            <div className="property-group">
              <label className="property-label"><i className="fa-solid fa-fill-drip" style={{ marginRight: "8px", color: "#6b7280" }} />Background Color</label>
              <div className="color-control-inputs" style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                <input type="color" className="property-input property-input-color" value={state.selectedElement?.style?.backgroundColor || "#3b82f6"} onChange={(e) => onPropertyChange("backgroundColor", e.target.value)} />
                <input type="text" className="property-input property-input-text color-input-text" value={state.selectedElement?.style?.backgroundColor || ""} onChange={(e) => onPropertyChange("backgroundColor", e.target.value)} placeholder="#3b82f6" style={{ flex: 1 }} />
              </div>
            </div>
            <div className="property-group">
              <label className="property-label"><i className="fa-solid fa-expand-arrows-alt" style={{ marginRight: "8px", color: "#6b7280" }} />Padding</label>
              <div className="property-input-wrapper">
                <input type="text" className="property-input spacing-input-text" value={state.selectedElement?.style?.padding || ""} onChange={(e) => onPropertyChange("padding", e.target.value)} placeholder="8px 16px" />
                <div className="property-input-arrows">
                  <button type="button" className="property-arrow-btn property-arrow-up" onClick={() => adjustNumericValue("padding", state.selectedElement?.style?.padding || "0px", "increment")}><i className="fa-solid fa-chevron-up" /></button>
                  <button type="button" className="property-arrow-btn property-arrow-down" onClick={() => adjustNumericValue("padding", state.selectedElement?.style?.padding || "0px", "decrement")}><i className="fa-solid fa-chevron-down" /></button>
                </div>
              </div>
            </div>
            <div className="property-group">
              <label className="property-label"><i className="fa-solid fa-border-style" style={{ marginRight: "8px", color: "#6b7280" }} />Border Width</label>
              <div className="property-input-wrapper">
                <input type="text" className="property-input border-input-text" value={parseBorder(state.selectedElement?.style?.border || "").width} onChange={(e) => onBorderPropertyChange("borderWidth", e.target.value)} placeholder="0px" />
                <div className="property-input-arrows">
                  <button type="button" className="property-arrow-btn property-arrow-up" onClick={() => adjustBorderWidth("increment")}><i className="fa-solid fa-chevron-up" /></button>
                  <button type="button" className="property-arrow-btn property-arrow-down" onClick={() => adjustBorderWidth("decrement")}><i className="fa-solid fa-chevron-down" /></button>
                </div>
              </div>
            </div>
            <div className="property-group">
              <label className="property-label"><i className="fa-solid fa-border-all" style={{ marginRight: "8px", color: "#6b7280" }} />Border Style</label>
              <select className="property-input property-input-select" value={parseBorder(state.selectedElement?.style?.border || "").style} onChange={(e) => onBorderPropertyChange("borderStyle", e.target.value)}>
                {["none","solid","dashed","dotted","double","groove","ridge","inset","outset"].map((s) => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
              </select>
            </div>
            <div className="property-group">
              <label className="property-label"><i className="fa-solid fa-palette" style={{ marginRight: "8px", color: "#6b7280" }} />Border Color</label>
              <div className="color-control-inputs" style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                <input type="color" className="property-input property-input-color" value={parseBorder(state.selectedElement?.style?.border || "").color || "#000000"} onChange={(e) => onBorderPropertyChange("borderColor", e.target.value)} />
                <input type="text" className="property-input property-input-text color-input-text" value={parseBorder(state.selectedElement?.style?.border || "").color || ""} onChange={(e) => onBorderPropertyChange("borderColor", e.target.value)} placeholder="#000000" style={{ flex: 1 }} />
              </div>
            </div>
            <div className="property-group">
              <label className="property-label"><i className="fa-solid fa-circle" style={{ marginRight: "8px", color: "#6b7280" }} />Border Radius</label>
              <div className="property-input-wrapper">
                <input type="text" className="property-input border-input-text" value={state.selectedElement?.style?.borderRadius || ""} onChange={(e) => onPropertyChange("borderRadius", e.target.value)} placeholder="4px" />
                <div className="property-input-arrows">
                  <button type="button" className="property-arrow-btn property-arrow-up" onClick={() => adjustNumericValue("borderRadius", state.selectedElement?.style?.borderRadius || "0px", "increment")}><i className="fa-solid fa-chevron-up" /></button>
                  <button type="button" className="property-arrow-btn property-arrow-down" onClick={() => adjustNumericValue("borderRadius", state.selectedElement?.style?.borderRadius || "0px", "decrement")}><i className="fa-solid fa-chevron-down" /></button>
                </div>
              </div>
            </div>
          </div>
        </AccordionSection>
      )}

      {canAssignOnClick && (
        <AccordionSection
          id="buttonOnClick"
          icon="fa-bolt"
          title="On Click"
          hint="Assign a workflow to run when this element is clicked."
          expanded={Boolean(state.expandedSections.buttonOnClick)}
          onToggle={onToggleSection}
        >
          <OrchestratorOnClickPanel
            projectId={projectId}
            uiEventSaving={uiEventSaving}
            value={{
              mode: onClickDraft.executionType === "SERVICE" ? "svc" : "orch",
              workflowId: onClickDraft.workflowId || "",
              workflowName: onClickDraft.workflowName || "",
              query: onClickDraft.query ?? onClickDraft.orchestrator?.query ?? "",
              serviceAppId: onClickDraft.service?.serviceAppId || "",
            }}
            onChange={(config) => {
              if (config?.remove || config?.clear) {
                setOnClickDraft(defaultOnClickDraft);
                onOrchestratorOnClickChange?.({ remove: true, clear: true });
                if (onUpsertElementEventConfig && selectorKey) {
                  const nextEvents = { ...(selectedElementEventConfig || {}) };
                  delete nextEvents.onClick;
                  onUpsertElementEventConfig(selectorKey, selector, nextEvents);
                }
                return;
              }

              const nextQuery = String(config.query ?? "");
              setOnClickDraft((prev) => ({
                ...prev,
                enabled: true,
                actionType: "workflow",
                executionType: config.executionType === "SERVICE" ? "SERVICE" : "ORCHESTRATOR",
                workflowId: config.workflowId || "",
                workflowName: config.workflowName || prev.workflowName || "",
                query: nextQuery,
                orchestrator: {
                  ...prev.orchestrator,
                  query: nextQuery,
                },
                service: {
                  ...prev.service,
                  serviceAppId: config.service?.serviceAppId || "",
                },
              }));
              if (config.executionType === "ORCHESTRATOR" && config.workflowId) {
                onOrchestratorOnClickChange?.({ ...config, query: nextQuery });
              }
            }}
          />
        </AccordionSection>
      )}

      {/* Typography */}
      {showTypography && (
        <AccordionSection id="typography" icon="fa-font" title="Typography" hint="Font size, weight, and text color." expanded={Boolean(state.expandedSections.typography)} onToggle={onToggleSection}>
          <div className="properties-form">
            <div className="property-group typography-row">
              <label className="property-label"><i className="fa-solid fa-font" style={{ marginRight: "8px", color: "#6b7280" }} />Typography</label>
              <div className="typography-container">
                <div className="typography-preview" style={{ fontSize: state.selectedElement?.style?.fontSize || "16px", fontWeight: state.selectedElement?.style?.fontWeight || "normal", fontFamily: state.selectedElement?.style?.fontFamily || "inherit", color: state.selectedElement?.style?.color || "#000000" }}>
                  <span className="typography-preview-text">Aa</span>
                  <span className="typography-preview-size">{state.selectedElement?.style?.fontSize || "16px"}</span>
                </div>
                <div className="typography-control-group">
                  <div className="typography-control-header"><span className="typography-control-label">Size</span></div>
                  <div className="property-input-wrapper">
                    <input type="text" className="property-input" value={state.selectedElement?.style?.fontSize || ""} onChange={(e) => onPropertyChange("fontSize", e.target.value)} placeholder="16px" />
                    <div className="property-input-arrows">
                      <button type="button" className="property-arrow-btn property-arrow-up" onClick={() => adjustNumericValue("fontSize", state.selectedElement?.style?.fontSize || "16px", "increment")}><i className="fa-solid fa-chevron-up" /></button>
                      <button type="button" className="property-arrow-btn property-arrow-down" onClick={() => adjustNumericValue("fontSize", state.selectedElement?.style?.fontSize || "16px", "decrement")}><i className="fa-solid fa-chevron-down" /></button>
                    </div>
                  </div>
                </div>
                <div className="typography-control-group">
                  <div className="typography-control-header"><span className="typography-control-label">Weight</span></div>
                  <select className="property-input property-input-select" value={state.selectedElement?.style?.fontWeight || "normal"} onChange={(e) => onPropertyChange("fontWeight", e.target.value)}>
                    {["normal","bold","100","200","300","400","500","600","700","800","900"].map((w) => <option key={w} value={w}>{w}</option>)}
                  </select>
                </div>
                <div className="typography-control-group">
                  <div className="typography-control-header"><span className="typography-control-label">Family</span></div>
                  <select className="property-input property-input-select" value={state.selectedElement?.style?.fontFamily || "inherit"} onChange={(e) => onPropertyChange("fontFamily", e.target.value)}>
                    <option value="inherit">Inherit</option>
                    <option value="Arial, sans-serif">Arial</option>
                    <option value="Georgia, serif">Georgia</option>
                    <option value="'Times New Roman', serif">Times New Roman</option>
                    <option value="'Courier New', monospace">Courier New</option>
                    <option value="Verdana, sans-serif">Verdana</option>
                    <option value="system-ui, sans-serif">System UI</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </AccordionSection>
      )}

      {/* Basic Info */}
      {showBasicInfo && (
        <AccordionSection id="basicInfo" icon="fa-sliders" title="Layout & Style" hint="Spacing, dimensions, and shared styles." expanded={Boolean(state.expandedSections.basicInfo)} onToggle={onToggleSection}>
          <div className="properties-form">
            {/* Display */}
            <div className="property-group">
              <label className="property-label"><i className="fa-solid fa-table-cells" style={{ marginRight: "8px", color: "#6b7280" }} />Display</label>
              <select
                className="property-input property-input-select"
                value={state.selectedElement?.style?.display || ""}
                onChange={(e) => onPropertyChange("display", e.target.value)}
              >
                <option value="">Default</option>
                {(() => {
                  const options = [
                    "block",
                    "inline",
                    "inline-block",
                    "flex",
                    "inline-flex",
                    "grid",
                    "inline-grid",
                    "none",
                    "contents",
                    "table",
                    "table-row",
                    "table-cell",
                    "list-item",
                  ];
                  const current = state.selectedElement?.style?.display || "";
                  if (current && !options.includes(current)) options.unshift(current);
                  return options.map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ));
                })()}
              </select>
            </div>

            {/* Dimensions */}
            <div className="property-group dimensions-row">
              <label className="property-label"><i className="fa-solid fa-arrows-alt" style={{ marginRight: "8px", color: "#6b7280" }} />Dimensions</label>
              <div className="dimensions-container">
                {["width","height"].map((dim) => (
                  <div className="dimensions-control-group" key={dim}>
                    <div className="dimensions-control-header"><span className="dimensions-control-label">{dim.charAt(0).toUpperCase() + dim.slice(1)}</span></div>
                    <div className="property-input-wrapper">
                      <input type="text" className="property-input dimensions-input-text" value={state.selectedElement?.style?.[dim] || ""} onChange={(e) => onPropertyChange(dim, e.target.value)} placeholder="auto" />
                      <div className="property-input-arrows">
                        <button type="button" className="property-arrow-btn property-arrow-up" onClick={() => adjustNumericValue(dim, state.selectedElement?.style?.[dim] || "0px", "increment")}><i className="fa-solid fa-chevron-up" /></button>
                        <button type="button" className="property-arrow-btn property-arrow-down" onClick={() => adjustNumericValue(dim, state.selectedElement?.style?.[dim] || "0px", "decrement")}><i className="fa-solid fa-chevron-down" /></button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Spacing */}
            <div className="property-group spacing-row">
              <label className="property-label"><i className="fa-solid fa-expand" style={{ marginRight: "8px", color: "#6b7280" }} />Spacing</label>
              <div className="spacing-container">
                {["padding","margin"].map((sp) => (
                  <div className="spacing-control-group" key={sp}>
                    <div className="spacing-control-header"><span className="spacing-control-label">{sp.charAt(0).toUpperCase() + sp.slice(1)}</span></div>
                    <div className="property-input-wrapper">
                      <input type="text" className="property-input spacing-input-text" value={state.selectedElement?.style?.[sp] || ""} onChange={(e) => onPropertyChange(sp, e.target.value)} placeholder="0px" />
                      <div className="property-input-arrows">
                        <button type="button" className="property-arrow-btn property-arrow-up" onClick={() => adjustNumericValue(sp, state.selectedElement?.style?.[sp] || "0px", "increment")}><i className="fa-solid fa-chevron-up" /></button>
                        <button type="button" className="property-arrow-btn property-arrow-down" onClick={() => adjustNumericValue(sp, state.selectedElement?.style?.[sp] || "0px", "decrement")}><i className="fa-solid fa-chevron-down" /></button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Border */}
            <div className="property-group border-row">
              <label className="property-label"><i className="fa-solid fa-border-style" style={{ marginRight: "8px", color: "#6b7280" }} />Border</label>
              <div className="border-container">
                <div className="border-control-group">
                  <div className="border-control-header"><span className="border-control-label">Style</span></div>
                  <input type="text" className="property-input border-input-text" value={state.selectedElement?.style?.border || ""} onChange={(e) => onPropertyChange("border", e.target.value)} placeholder="1px solid #000" />
                </div>
                <div className="border-control-group">
                  <div className="border-control-header"><span className="border-control-label">Radius</span></div>
                  <div className="property-input-wrapper">
                    <input type="text" className="property-input border-input-text" value={state.selectedElement?.style?.borderRadius || ""} onChange={(e) => onPropertyChange("borderRadius", e.target.value)} placeholder="0px" />
                    <div className="property-input-arrows">
                      <button type="button" className="property-arrow-btn property-arrow-up" onClick={() => adjustNumericValue("borderRadius", state.selectedElement?.style?.borderRadius || "0px", "increment")}><i className="fa-solid fa-chevron-up" /></button>
                      <button type="button" className="property-arrow-btn property-arrow-down" onClick={() => adjustNumericValue("borderRadius", state.selectedElement?.style?.borderRadius || "0px", "decrement")}><i className="fa-solid fa-chevron-down" /></button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Colors */}
            <div className="property-group colors-row">
              <label className="property-label"><i className="fa-solid fa-palette" style={{ marginRight: "8px", color: "#6b7280" }} />Colors</label>
              <div className="colors-container">
                <div className="color-preview-swatch" style={{ background: state.selectedElement?.style?.backgroundColor || "#ffffff", color: state.selectedElement?.style?.color || "#000000" }}>
                  <span className="color-preview-text">Aa</span>
                </div>
                {[{ label: "Text", prop: "color", default: "#000000" }, { label: "Background", prop: "backgroundColor", default: "#ffffff" }].map(({ label, prop, default: def }) => (
                  <div className="color-control-group" key={prop}>
                    <div className="color-control-header"><span className="color-control-label">{label}</span></div>
                    <div className="color-control-inputs">
                      <input type="color" className="property-input property-input-color" value={state.selectedElement?.style?.[prop] || def} onChange={(e) => onPropertyChange(prop, e.target.value)} />
                      <input type="text" className="property-input property-input-text color-input-text" value={state.selectedElement?.style?.[prop] || ""} onChange={(e) => onPropertyChange(prop, e.target.value)} placeholder={def} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Text Align */}
            {showTypography && (
              <div className="property-group">
                <label className="property-label"><i className="fa-solid fa-align-left" style={{ marginRight: "8px", color: "#6b7280" }} />Text Align</label>
                <select className="property-input property-input-select" value={state.selectedElement?.style?.textAlign || "left"} onChange={(e) => onPropertyChange("textAlign", e.target.value)}>
                  {["left","center","right","justify"].map((a) => <option key={a} value={a}>{a.charAt(0).toUpperCase() + a.slice(1)}</option>)}
                </select>
              </div>
            )}
          </div>
        </AccordionSection>
      )}
    </>
  );
}

function PropertiesPanelAdvanced({
  state, setState, onToggleSection, onDrop, onDragOver, onFileInputChange, onImageUpload,
  onUpdateImageFilter, applyCrop, applyTransform, onResetFilters, ImageSlider, buildImageFilter,
  formatMBLabel, allowedImageMimeTypes,
}) {
  const imageSrc = state.selectedElement?.imageContent;
  const resolvedImageSrc = !imageSrc ? ""
    : imageSrc.startsWith("data:") || imageSrc.startsWith("http://") || imageSrc.startsWith("https://") ? imageSrc
    : state.previewUrl ? `${state.previewUrl.replace(/\/$/, "")}/${imageSrc.replace(/^\//, "")}` : imageSrc;

  return (
    <>
      <AccordionSection id="uploadImage" icon="fa-image" title="Upload Image" expanded={Boolean(state.expandedSections.uploadImage)} onToggle={onToggleSection}>
        <div className="image-upload-section">
          {state.selectedElement?.imageContent && (
            <div style={{ marginBottom: "16px", textAlign: "center" }}>
              <div style={{ padding: "12px", borderRadius: "12px", background: "linear-gradient(145deg, #f0f4ff 0%, #e8eef9 100%)", border: "1px solid #c7d2fe", display: "inline-block" }}>
                <span style={{ display: "inline-block", fontSize: "11px", fontWeight: "600", color: "#4f46e5", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "10px" }}>Preview</span>
                <div style={{ borderRadius: "8px", overflow: "hidden", backgroundColor: "#fff", padding: "8px" }}>
                  <img src={resolvedImageSrc} alt="Uploaded preview" style={{ display: "block", maxWidth: "200px", maxHeight: "160px", objectFit: "contain" }} onError={(e) => { e.target.style.display = "none"; }} />
                </div>
              </div>
            </div>
          )}
          <div onDrop={onDrop} onDragOver={onDragOver} style={{ border: "2px dashed #d1d5db", borderRadius: "8px", padding: "24px", textAlign: "center", cursor: "pointer", backgroundColor: state.uploadingImage ? "#f9fafb" : "#ffffff", transition: "all 0.2s ease", minHeight: "120px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "12px" }}
            onMouseEnter={(e) => { if (!state.uploadingImage) { e.currentTarget.style.borderColor = "#3b82f6"; e.currentTarget.style.backgroundColor = "#f0f9ff"; } }}
            onMouseLeave={(e) => { if (!state.uploadingImage) { e.currentTarget.style.borderColor = "#d1d5db"; e.currentTarget.style.backgroundColor = "#ffffff"; } }}
            onClick={() => { if (!state.uploadingImage) document.getElementById("image-file-input")?.click(); }}
          >
            {state.uploadingImage ? (
              <><PiSpinnerGapLight className="fa-solid fa-spinner fa-spin" style={{ fontSize: "24px", color: "#3b82f6" }} /><p style={{ margin: 0, color: "#6b7280", fontSize: "13px" }}>Uploading image...</p></>
            ) : (
              <><i className="fa-solid fa-cloud-arrow-up" style={{ fontSize: "32px", color: "#9ca3af", marginBottom: "8px" }} /><p style={{ margin: 0, color: "#6b7280", fontSize: "13px", fontWeight: "500" }}>Drag and drop an image here, or click to select</p><p style={{ margin: "4px 0 0 0", color: "#9ca3af", fontSize: "12px" }}>Supports: JPG, PNG, GIF, WebP, SVG (max {formatMBLabel})</p></>
            )}
          </div>
          <input id="image-file-input" type="file" accept={allowedImageMimeTypes.join(", ")} onChange={onFileInputChange} style={{ display: "none" }} />
        </div>
      </AccordionSection>

      <AccordionSection id="imageProperties" icon="fa-sliders" title="Image Properties" expanded={Boolean(state.expandedSections.imageProperties)} onToggle={onToggleSection}>
        <div style={{ fontSize: "10px", fontWeight: "600", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "10px" }}>Light</div>
        <ImageSlider label="Exposure"   min={50} max={150} value={state.imageFilters.exposure}   onChange={(v) => onUpdateImageFilter("exposure",   v)} />
        <ImageSlider label="Brightness" min={50} max={150} value={state.imageFilters.brightness} onChange={(v) => onUpdateImageFilter("brightness", v)} />
        <ImageSlider label="Contrast"   min={50} max={150} value={state.imageFilters.contrast}   onChange={(v) => onUpdateImageFilter("contrast",   v)} />
        <div style={{ fontSize: "10px", fontWeight: "600", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "10px", marginTop: "16px" }}>Color</div>
        <ImageSlider label="Saturation" min={0}  max={200} value={state.imageFilters.saturation} onChange={(v) => onUpdateImageFilter("saturation", v)} />
        <ImageSlider label="Hue"        min={0}  max={360} value={state.imageFilters.hue}        onChange={(v) => onUpdateImageFilter("hue",        v)} />
        <div style={{ fontSize: "10px", fontWeight: "600", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "10px", marginTop: "16px" }}>Effect</div>
        <ImageSlider label="Blur"       min={0}  max={10}  value={state.imageFilters.blur}       onChange={(v) => onUpdateImageFilter("blur",       v)} />
      </AccordionSection>

      <AccordionSection id="rotate" icon="fa-rotate" title="Rotate" expanded={Boolean(state.expandedSections.rotate)} onToggle={onToggleSection}>
        <ImageSlider label="Angle" min={-180} max={180} value={state.transform?.rotate || 0}
          onChange={(value) => { setState((prev) => ({ ...prev, transform: { ...prev.transform, rotate: value } })); applyTransform(); }}
          formatValue={(val) => `${val}°`} />
      </AccordionSection>

      <AccordionSection id="crop" icon="fa-crop" title="Crop" expanded={Boolean(state.expandedSections.crop)} onToggle={onToggleSection}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
          {["top","right","bottom","left"].map((side) => (
            <ImageSlider key={side} label={side.charAt(0).toUpperCase() + side.slice(1)} min={0} max={50} value={state.crop?.[side] || 0}
              onChange={(value) => { const next = { ...state.crop, [side]: value }; setState((prev) => ({ ...prev, crop: next })); applyCrop(next); }} />
          ))}
        </div>
      </AccordionSection>

      <div className="design-drawer-section" style={{ marginTop: "16px" }}>
        <button type="button" onClick={onResetFilters} style={{ width: "100%", padding: "10px 14px", cursor: "pointer", fontSize: "13px", fontWeight: "500", color: "#6b7280", background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: "8px" }}>
          <i className="fa-solid fa-rotate-left" style={{ marginRight: "8px", color: "#9ca3af" }} />Reset all
        </button>
      </div>
    </>
  );
}

PropertiesPanel.propTypes = {
  state: PropTypes.object.isRequired,
  setState: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onToggleSection: PropTypes.func.isRequired,
  onTextContentChange: PropTypes.func.isRequired,
  onPropertyChange: PropTypes.func.isRequired,
  onIconChange: PropTypes.func,
  parseBorder: PropTypes.func.isRequired,
  onBorderPropertyChange: PropTypes.func.isRequired,
  adjustBorderWidth: PropTypes.func.isRequired,
  adjustNumericValue: PropTypes.func.isRequired,
  onDrop: PropTypes.func.isRequired,
  onDragOver: PropTypes.func.isRequired,
  onFileInputChange: PropTypes.func.isRequired,
  onImageUpload: PropTypes.func.isRequired,
  onUpdateImageFilter: PropTypes.func.isRequired,
  applyCrop: PropTypes.func.isRequired,
  applyTransform: PropTypes.func.isRequired,
  onResetFilters: PropTypes.func.isRequired,
  formatMB: PropTypes.func,
  maxImageUploadSize: PropTypes.number,
  allowedImageMimeTypes: PropTypes.array,

  // UI event mappings + workflows
  availableWorkflows: PropTypes.array,
  availableServiceApps: PropTypes.array,
  uiEventSaving: PropTypes.bool,
  selectedElementSelectorKey: PropTypes.string,
  selectedElementEventConfig: PropTypes.object,
  onUpsertElementEventConfig: PropTypes.func,
  projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onOrchestratorOnClickChange: PropTypes.func,
};

export default PropertiesPanel;